import type { ElementType, ReactNode } from 'react'
import type { ModuleConfig } from '@/types/modules'
import { isPreviewFrontend } from '@/lib/productionHost'
import {
  Activity,
  BarChart3,
  Briefcase,
  Building2,
  Clock,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react'

/** Sidebar navigation config. Lives outside Sidebar.tsx so the component
 *  file exports components only (Fast Refresh) and the route-preload test
 *  can read the destinations. */
export interface NavChild {
  label: string
  href: string
  badge?: ReactNode
  module?: keyof ModuleConfig
  hideOnProduction?: boolean
}

export interface NavItem {
  label: string
  href: string
  icon: ElementType
  roles?: string[]
  isSection?: boolean
  module?: keyof ModuleConfig
  children?: NavChild[]
  badge?: ReactNode
}

export function visibleNavChildren(
  children: NavChild[],
  opts: {
    moduleConfig?: ModuleConfig | null
    previewFrontend?: boolean
  } = {},
): NavChild[] {
  const preview = opts.previewFrontend ?? isPreviewFrontend()
  return children.filter((child) => {
    if (child.hideOnProduction && !preview) return false
    if (child.module && !opts.moduleConfig?.[child.module]) return false
    return true
  })
}

export const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Contacts',
    href: '/customers',
    icon: Users,
    children: [
      { label: 'Active Contacts', href: '/customers' },
      { label: 'Search Contacts', href: '/customers/search' },
      { label: 'Contact Lists', href: '/customers/lists' },
    ],
  },
  {
    label: 'Action Center',
    href: '/action-center',
    icon: Zap,
    roles: ['collections_agent', 'tenant_admin'],
  },
  {
    label: 'Payments',
    href: '/collections/pending-payments',
    icon: Clock,
    roles: ['collections_agent', 'tenant_admin'],
  },
  {
    label: 'Manage',
    href: '#manage',
    icon: Wrench,
    isSection: true,
    roles: ['tenant_admin'],
  },
  {
    label: 'Reports',
    href: '/risk',
    icon: BarChart3,
    roles: ['tenant_admin'],
    children: [
      { label: 'Cash Flow', href: '/forecast' },
      { label: 'Deferred Revenue', href: '/reports/deferred-revenue', module: 'membership_expiration' },
      { label: 'Risk Scoring', href: '/risk' },
      { label: 'Card Attempts', href: '/payment-attempts', hideOnProduction: true },
    ],
  },
  {
    label: 'Workflows',
    href: '/flows',
    icon: Workflow,
    roles: ['tenant_admin'],
    children: [
      { label: 'Flows', href: '/flows' },
      { label: 'Pending Tasks', href: '/pending-tasks' },
      { label: 'Activity Log', href: '/activity-log' },
    ],
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
    roles: ['tenant_admin'],
  },
]

export const superAdminNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Brand Comparison', href: '/brand-comparison', icon: Building2 },
  { label: 'Organizations', href: '/organizations', icon: Building2 },
  { label: 'Contacts', href: '/customers', icon: Users },
  { label: 'Human Tasks', href: '/human-tasks', icon: Briefcase },
  { label: 'Super Admins', href: '/super-admins', icon: ShieldCheck },
  { label: 'System Settings', href: '/system-settings', icon: Settings },
  { label: 'Operational Limits', href: '/operational-config', icon: SlidersHorizontal },
  { label: 'Operations', href: '/operations', icon: Activity },
]

